import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useStore from '../hooks/useStore'

function FocusPanel() {
  const navigate = useNavigate()
  const focusShellRef = useRef(null)
  const bellTimeoutsRef = useRef([])
  const {
    goal,
    monthlyRecord,
    wScore,
    cScore,
    tScore,
    timerSec,
    timerMax,
    timerRunning,
    timerStartedAt,
    timerEndAt,
    activeDur,
    blocksToday,
    focusMinutesTotal,
    focusMode,
    focusContextHidden,
    updateFormData,
    setTimer,
    addBlock,
    logFocusMinutes,
    setFocusMode,
    setFocusContextHidden,
    showToast,
    saveFocusDraft,
  } = useStore()

  const tDraftTimer = useRef(null)

  const [blocks, setBlocks] = useState([])

  useEffect(() => {
    let interval
    if (timerRunning && timerEndAt) {
      const syncTimer = () => {
        const remaining = Math.max(0, Math.ceil((timerEndAt - Date.now()) / 1000))
        setTimer({ timerSec: remaining })
      }

      syncTimer()
      interval = setInterval(syncTimer, 1000)
      document.addEventListener('visibilitychange', syncTimer)

      return () => {
        clearInterval(interval)
        document.removeEventListener('visibilitychange', syncTimer)
      }
    }

    return () => {}
  }, [timerRunning, timerEndAt, setTimer])

  useEffect(() => {
    if (timerRunning && timerSec <= 0) {
      handleTimerComplete()
    }
  }, [timerRunning, timerSec])

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && focusMode) {
        setFocusMode(false)
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [focusMode, setFocusMode])

  useEffect(() => {
    return () => {
      bellTimeoutsRef.current.forEach(clearTimeout)
    }
  }, [])

  const playCompletionBell = () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      if (!AudioContextClass) return

      const context = new AudioContextClass()
      const now = context.currentTime
      const notes = [
        { frequency: 880, delay: 0, duration: 0.18 },
        { frequency: 1174.66, delay: 0.16, duration: 0.22 },
        { frequency: 1567.98, delay: 0.34, duration: 0.32 }
      ]

      notes.forEach((note) => {
        const oscillator = context.createOscillator()
        const gain = context.createGain()

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(note.frequency, now + note.delay)
        gain.gain.setValueAtTime(0.0001, now + note.delay)
        gain.gain.exponentialRampToValueAtTime(0.18, now + note.delay + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + note.delay + note.duration)

        oscillator.connect(gain)
        gain.connect(context.destination)

        oscillator.start(now + note.delay)
        oscillator.stop(now + note.delay + note.duration)
      })

      const timeoutId = window.setTimeout(() => {
        context.close().catch(() => {})
      }, 1200)
      bellTimeoutsRef.current.push(timeoutId)
    } catch (error) {
      console.error('Bell sound error:', error)
    }
  }

  const playStartBell = () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      if (!AudioContextClass) return

      const context = new AudioContextClass()
      const now = context.currentTime
      const notes = [
        { frequency: 523.25, delay: 0, duration: 0.12, type: 'triangle' },
        { frequency: 659.25, delay: 0.1, duration: 0.14, type: 'triangle' },
        { frequency: 783.99, delay: 0.2, duration: 0.2, type: 'sine' }
      ]

      notes.forEach((note) => {
        const oscillator = context.createOscillator()
        const gain = context.createGain()

        oscillator.type = note.type
        oscillator.frequency.setValueAtTime(note.frequency, now + note.delay)
        gain.gain.setValueAtTime(0.0001, now + note.delay)
        gain.gain.exponentialRampToValueAtTime(0.12, now + note.delay + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + note.delay + note.duration)

        oscillator.connect(gain)
        gain.connect(context.destination)

        oscillator.start(now + note.delay)
        oscillator.stop(now + note.delay + note.duration)
      })

      const timeoutId = window.setTimeout(() => {
        context.close().catch(() => {})
      }, 900)
      bellTimeoutsRef.current.push(timeoutId)
    } catch (error) {
      console.error('Start bell sound error:', error)
    }
  }

  const handleTimerComplete = () => {
    try {
      setTimer({ timerRunning: false, timerSec: timerMax, timerStartedAt: null, timerEndAt: null })
      setFocusMode(false)
      addBlock(Math.round(timerMax / 60))
      saveFocusDraft()
      playCompletionBell()
      showToast('🎯', 'Block complete! Well done.')
      addBlockToLog(activeDur + '-min deep work block', 'Auto')
      exitFullscreen()
    } catch (error) {
      console.error('Timer complete error:', error)
    }
  }

  const addBlockToLog = (label, type) => {
    const now = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    const newBlock = {
      time: now,
      label,
      type
    }
    setBlocks(prev => [newBlock, ...prev])
  }

  const handleCScoreChange = (value) => {
    updateFormData({ cScore: parseInt(value, 10) })
  }

  const handleTScoreChange = (value) => {
    updateFormData({ tScore: parseInt(value, 10) })
    clearTimeout(tDraftTimer.current)
    tDraftTimer.current = setTimeout(() => saveFocusDraft(), 1500)
  }

  const setDuration = (minutes) => {
    setTimer({
      activeDur: minutes,
      timerMax: minutes * 60,
      timerSec: minutes * 60,
      timerRunning: false,
      timerStartedAt: null,
      timerEndAt: null,
    })
  }

  const enterFullscreen = async () => {
    try {
      const element = focusShellRef.current || document.documentElement
      if (element?.requestFullscreen && !document.fullscreenElement) {
        await element.requestFullscreen()
      }
    } catch (error) {
      console.error('Enter fullscreen error:', error)
    }
  }

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen()
      }
    } catch (error) {
      console.error('Exit fullscreen error:', error)
    }
  }

  const openFocusMode = async () => {
    setFocusMode(true)
    setFocusContextHidden(false)
    await enterFullscreen()
  }

  const minimizeFocusMode = async () => {
    setFocusMode(false)
    await exitFullscreen()
  }

  const toggleTimer = async () => {
    if (timerRunning) {
      const remaining = timerEndAt ? Math.max(0, Math.ceil((timerEndAt - Date.now()) / 1000)) : timerSec
      setTimer({ timerRunning: false, timerSec: remaining, timerStartedAt: null, timerEndAt: null })
      return
    }

    const now = Date.now()
    setTimer({ timerRunning: true, timerStartedAt: now, timerEndAt: now + timerSec * 1000 })
    playStartBell()
    await openFocusMode()
  }

  const resetTimer = () => {
    const elapsedSeconds = timerStartedAt ? Math.floor((Date.now() - timerStartedAt) / 1000) : timerMax - timerSec
    const minsRan = Math.floor(Math.max(0, Math.min(timerMax, elapsedSeconds)) / 60)
    if (minsRan > 0) {
      logFocusMinutes(minsRan)
      saveFocusDraft()
    }
    setTimer({ timerRunning: false, timerSec: timerMax, timerStartedAt: null, timerEndAt: null })
  }

  const logManualBlock = () => {
    addBlock(activeDur)
    saveFocusDraft()
    addBlockToLog(activeDur + '-min manual block', 'Manual')
    showToast('✓', 'Block logged!')
  }

  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0')
    const secs = String(seconds % 60).padStart(2, '0')
    return `${mins}:${secs}`
  }

  const getTimerRingOffset = () => {
    return 490 * (1 - timerSec / timerMax)
  }

  const monthlyGoalText = monthlyRecord?.monthly_goal || monthlyGoalFallback()

  function monthlyGoalFallback() {
    const label = new Date(2026, 0, 1)
    return monthlyRecord?.monthly_goal || 'No monthly goal set yet — visit Monthly Results Area first.'
  }

  const renderTimerCard = (immersive = false) => (
    <div className={`card mb24 ${immersive ? 'focus-timer-card immersive' : ''}`}>
      <div className="cg sage"></div>
      <div className={`focus-timer-layout ${immersive ? 'immersive' : ''}`}>
        <div className="focus-timer-ring-wrap">
          <svg className="focus-ring-svg" style={{ transform: 'rotate(-90deg)' }} width="180" height="180" viewBox="0 0 180 180">
            <circle cx="90" cy="90" r="78" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6"/>
            <circle
              cx="90"
              cy="90"
              r="78"
              fill="none"
              stroke={timerSec < 300 ? 'var(--coral2)' : 'var(--sage2)'}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray="490"
              strokeDashoffset={getTimerRingOffset()}
              style={{
                filter: 'drop-shadow(0 0 8px rgba(128,232,196,0.5))',
                transition: 'stroke-dashoffset 1s linear, stroke 0.5s'
              }}
            />
          </svg>
          <div className="focus-ring-center">
            <div className={`tt ${timerSec < 300 ? 'warn' : ''}`}>{formatTime(timerSec)}</div>
            <div className="tl">{timerRunning ? 'in progress' : timerSec === timerMax ? 'ready' : 'paused'}</div>
          </div>
        </div>
        <div className="focus-controls">
          <div className="c-lbl">Duration</div>
          <div className="focus-duration-row">
            {[90, 60, 45, 25].map((min) => (
              <button
                key={min}
                className={`btn ${activeDur === min ? 'bg-sage' : 'bg-ghost'}`}
                onClick={() => setDuration(min)}
              >
                {min} min
              </button>
            ))}
          </div>
          <div className="flex gap12">
            <button className="btn bg-sage" onClick={toggleTimer}>
              {timerRunning ? '⏸ Pause' : '▶ Start'}
            </button>
            <button className="btn bg-ghost" onClick={resetTimer}>
              ↺ Reset
            </button>
            {immersive ? (
              <button className="btn bg-ghost" onClick={minimizeFocusMode}>
                ↙ Minimize
              </button>
            ) : null}
          </div>
          <div className="mt16 focus-meta">
            Blocks today: <span className="focus-meta-value">{blocksToday}</span>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className="panel active">
        <div className="pg-ey">Step 02 · Deep Work</div>
        <div className="pg-ti">Focus Blocks — <em>C</em> &amp; <em>T</em></div>
        <div className="pg-su">
          Concentration without protected time is wishful thinking. Set both, start the timer, and let the formula work.
        </div>
        <div className="card mb24" style={{background: 'var(--bg1)'}}>
          <div className="c-lbl">Today's W — Active Goal</div>
          <div
            style={{
              fontFamily: 'var(--ff-d)',
              fontStyle: 'italic',
              fontSize: '18px',
              color: goal ? 'var(--azure2)' : 'var(--text3)',
              lineHeight: '1.5'
            }}
          >
            {goal || 'No goal set — visit Morning Setup first.'}
          </div>
          <div style={{fontSize: '10px', color: 'var(--text3)', fontFamily: 'var(--ff-m)', marginTop: '8px'}}>
            W CLARITY: <span style={{color: 'var(--azure2)'}}>{wScore}</span>/10
          </div>
        </div>
        <div className="g2 mb24">
          <div className="card">
            <div className="cg sage"></div>
            <div className="c-lbl">C — Concentration</div>
            <div className="var-badge sage">C</div>
            <div style={{fontSize: '13px', color: 'var(--text2)', lineHeight: '1.6', marginBottom: '4px'}}>
              Auto-calculated from your focus blocks today.
            </div>
            <div className="sw">
              <div className="sh">
                <span className="sq">{Math.round(focusMinutesTotal)}m of focus</span>
                <span className="sn sage">{cScore}</span>
              </div>
              <div className="st" style={{pointerEvents:'none'}}>
                <div className="sf2 sage" style={{width: `${cScore * 10}%`}}></div>
                <input type="range" min="0" max="10" value={cScore} readOnly className="sr"
                  style={{opacity:0.4,cursor:'default'}} onChange={() => {}} />
              </div>
              <div className="slbls">
                <span>0 min</span>
                <span>360+ min = 10</span>
              </div>
            </div>
            <div style={{fontSize:'10px',color:'var(--text3)',fontFamily:'var(--ff-m)',marginTop:'6px',letterSpacing:'0.08em'}}>
              45m=2 · 90m=4 · 180m=6 · 270m=8 · 360m=10
            </div>
          </div>
          <div className="card">
            <div className="cg coral"></div>
            <div className="c-lbl">T — Time Without Distractions</div>
            <div className="var-badge coral">T</div>
            <div style={{fontSize: '13px', color: 'var(--text2)', lineHeight: '1.6', marginBottom: '4px'}}>
              How well protected is your environment right now?
            </div>
            <div className="sw">
              <div className="sh">
                <span className="sq">Rate your environment</span>
                <span className="sn coral">{tScore}</span>
              </div>
              <div className="st">
                <div className="sf2 coral" style={{width: `${tScore * 10}%`}}></div>
                <input type="range" min="1" max="10" value={tScore} className="cr" onChange={(e) => handleTScoreChange(e.target.value)} />
              </div>
              <div className="slbls">
                <span>Distracted</span>
                <span>Fully protected</span>
              </div>
            </div>
          </div>
        </div>
        <div className="sec">Focus Timer</div>
        {renderTimerCard(false)}
        <div className="sec">Completed Blocks</div>
        <div style={{fontSize: '12px', color: 'var(--text3)', fontStyle: 'italic', padding: '12px 0'}}>
          {blocks.length === 0 ? 'No blocks yet — start your first timer.' : ''}
        </div>
        {blocks.map((block, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '12px 16px',
              background: 'var(--bg2)',
              border: '1px solid rgba(78,203,160,0.15)',
              borderRadius: 'var(--r)',
              marginBottom: '8px'
            }}
          >
            <span style={{color: 'var(--sage2)', fontSize: '18px'}}>✓</span>
            <span style={{fontFamily: 'var(--ff-m)', fontSize: '10px', color: 'var(--text3)', minWidth: '56px'}}>
              {block.time}
            </span>
            <span style={{flex: 1, fontSize: '13px', color: 'var(--text)'}}>{block.label}</span>
            <span style={{fontSize: '9px', letterSpacing: '0.15em', color: 'var(--sage2)', background: 'var(--sagefog)', padding: '4px 10px', borderRadius: '10px'}}>
              {block.type}
            </span>
          </div>
        ))}
        <div className="mt24">
          <button className="btn bg-sage" onClick={logManualBlock}>
            + Log Manual Block
          </button>
        </div>
      </div>

      {focusMode ? (
        <div className="focus-immersive-shell" ref={focusShellRef}>
          <div className="focus-immersive-topbar">
            <div>
              <div className="focus-immersive-label">Focus Window</div>
              <div className="focus-immersive-sub">{timerRunning ? 'Deep work in progress' : 'Ready to begin'}</div>
            </div>
            <div className="focus-immersive-actions">
              <button className="btn bg-ghost" onClick={() => setFocusContextHidden(!focusContextHidden)}>
                {focusContextHidden ? 'Show Goals' : 'Hide Goals'}
              </button>
              <button className="btn bg-ghost" onClick={minimizeFocusMode}>
                Minimize
              </button>
            </div>
          </div>
          <div className={`focus-immersive-body ${focusContextHidden ? 'context-hidden' : ''}`}>
            {!focusContextHidden ? (
              <aside className="focus-context-panel">
                <div className="focus-context-block">
                  <div className="c-lbl">Current Month Goal</div>
                  <div className="focus-context-goal">
                    {monthlyGoalText}
                  </div>
                </div>
                <div className="focus-context-block">
                  <div className="c-lbl">Winning Result For Today</div>
                  <div className="focus-context-goal w-goal">
                    {goal || 'No W set yet — visit Morning Setup first.'}
                  </div>
                  <div className="focus-context-score">
                    W clarity: <span>{wScore}</span>/10
                  </div>
                </div>
                <div className="focus-context-block focus-tricks-block">
                  <div className="c-lbl">3 Proven High Performance Tricks</div>
                  <div className="focus-trick-item">
                    Start with one outcome only.
                  </div>
                  <div className="focus-trick-item">
                    Put your phone out of reach before the first minute.
                  </div>
                  <div className="focus-trick-item">
                    When distracted, return to the next visible action immediately.
                  </div>
                </div>
              </aside>
            ) : null}
            <div className="focus-immersive-main">
              {renderTimerCard(true)}
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default FocusPanel
